'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type {
  EncounterContributionType,
} from '@/components/community/feed/types'

import type {
  SamanthaPageContext,
} from '@/hooks/useSamanthaPageContext'

/* -------------------------------------------------------------------------- */
/*                                   TYPES                                    */
/* -------------------------------------------------------------------------- */

export type ContextPublishProduct = {
  id: number
  name: string
  price: number
  priceLabel: string
}

export type ContextPublishFile = {
  id: string
  file: File
  previewUrl: string | null
  isImage: boolean
}

export type ContextPublishStatus =
  | 'idle'
  | 'publishing'
  | 'success'
  | 'error'

export type OpenContextPublishComposerArgs = {
  initialContent?: string

  contributionType?:
    | EncounterContributionType
    | null
}

export type ContextPublishResult = {
  post: any

  spaceId: number
  encounterId: number | null

  contributionType:
    | EncounterContributionType
    | null
}

type UseContextPublishComposerArgs = {
  sellerId:
    | number
    | null
    | undefined

  context:
    SamanthaPageContext

  /*
   * Más adelante desde AIMasterWidget
   * pasaremos esto según permisos.
   */
  canUsePremiumContent?:
    boolean

  onPublished?: (
    result:
      ContextPublishResult
  ) => void
}

/* -------------------------------------------------------------------------- */
/*                                  HELPERS                                   */
/* -------------------------------------------------------------------------- */

const MAX_FILES = 10

const ACCEPTED_FILE_TYPES = [
  'image/',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'text/plain',
  'video/mp4',
  'audio/mpeg',
]

function isAcceptedFile(
  file: File
) {
  const type =
    String(
      file.type || ''
    ).toLowerCase()

  /*
   * Algunos navegadores no reportan MIME
   * para zip/doc/etc.
   *
   * La UI además usará accept="".
   */
  if (!type) {
    return true
  }

  return ACCEPTED_FILE_TYPES.some(
    accepted =>
      accepted.endsWith('/')
        ? type.startsWith(
            accepted
          )
        : type === accepted
  )
}

function isImageFile(
  file: File
) {
  return String(
    file.type || ''
  ).startsWith(
    'image/'
  )
}

function fileIdentity(
  file: File
) {
  return [
    file.name,
    file.size,
    file.lastModified,
  ].join(':')
}

function makeComposerFile(
  file: File
): ContextPublishFile {
  const image =
    isImageFile(file)

  return {
    id:
      `${fileIdentity(file)}:${Math.random()
        .toString(36)
        .slice(2, 7)}`,

    file,

    isImage:
      image,

    previewUrl:
      image
        ? URL.createObjectURL(
            file
          )
        : null,
  }
}

/* -------------------------------------------------------------------------- */
/*                                  UPLOAD                                    */
/* -------------------------------------------------------------------------- */

async function uploadCommunityFile(
  file: File,
  sellerId: number
) {
  const presignRes =
    await fetch(
      '/api/uploads/member/presign',
      {
        method:
          'POST',

        credentials:
          'include',

        headers: {
          'Content-Type':
            'application/json',
        },

        body:
          JSON.stringify({
            scope:
              'community_feed',

            resourceId:
              String(
                sellerId
              ),

            filename:
              file.name,

            contentType:
              file.type ||
              'application/octet-stream',

            size:
              file.size,
          }),
      }
    )

  const presignJson =
    await presignRes
      .json()
      .catch(
        () =>
          null
      )

  if (
    !presignRes.ok ||
    !presignJson?.ok
  ) {
    throw new Error(
      presignJson?.error ||
        'No se pudo preparar la subida.'
    )
  }

  const putRes =
    await fetch(
      presignJson.uploadUrl,
      {
        method:
          'PUT',

        headers: {
          'Content-Type':
            presignJson.contentType ||
            file.type ||
            'application/octet-stream',
        },

        body:
          file,
      }
    )

  if (!putRes.ok) {
    throw new Error(
      `No se pudo subir ${file.name}.`
    )
  }

  const commitRes =
    await fetch(
      '/api/uploads/member/commit',
      {
        method:
          'POST',

        credentials:
          'include',

        headers: {
          'Content-Type':
            'application/json',
        },

        body:
          JSON.stringify({
            key:
              presignJson.key,

            scope:
              'community_feed',

            resourceId:
              String(
                sellerId
              ),
          }),
      }
    )

  const commitJson =
    await commitRes
      .json()
      .catch(
        () =>
          null
      )

  if (
    !commitRes.ok ||
    !commitJson?.ok
  ) {
    throw new Error(
      commitJson?.error ||
        'No se pudo confirmar la subida.'
    )
  }

  return {
    fileattach:
      presignJson.key,

    fileattach_orig:
      file.name,

    url:
      commitJson?.url ||
      `/api/uploads/member/view?key=${encodeURIComponent(
        presignJson.key
      )}`,
  }
}

/* -------------------------------------------------------------------------- */
/*                                   HOOK                                     */
/* -------------------------------------------------------------------------- */

export function useContextPublishComposer({
  sellerId,
  context,
  canUsePremiumContent =
    false,
  onPublished,
}: UseContextPublishComposerArgs) {
  const [
    open,
    setOpen,
  ] =
    useState(false)

  const [
    title,
    setTitle,
  ] =
    useState('')

  const [
    content,
    setContent,
  ] =
    useState('')

  const [
    contributionType,
    setContributionType,
  ] =
    useState<
      EncounterContributionType | null
    >(null)

  const [
    suggestedEncounterId,
    setSuggestedEncounterId,
  ] =
    useState<number | null>(
      null
    )

  const [
    suggestedContributionType,
    setSuggestedContributionType,
  ] =
    useState<
      EncounterContributionType | null
    >(null)

  const [
    files,
    setFiles,
  ] =
    useState<
      ContextPublishFile[]
    >([])

  const [
    selectedProductId,
    setSelectedProductId,
  ] =
    useState<number | null>(
      null
    )

  const [
    products,
    setProducts,
  ] =
    useState<
      ContextPublishProduct[]
    >([])

  const [
    productsLoading,
    setProductsLoading,
  ] =
    useState(false)

  const [
    status,
    setStatus,
  ] =
    useState<ContextPublishStatus>(
      'idle'
    )

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null
    )

  /*
   * Nos permite limpiar object URLs
   * incluso cuando files cambia.
   */
  const filesRef =
    useRef<
      ContextPublishFile[]
    >([])

  useEffect(() => {
    filesRef.current =
      files
  }, [
    files,
  ])

  /* ---------------------------------------------------------------------- */
  /*                              CONTEXT                                   */
  /* ---------------------------------------------------------------------- */

  const isSpace =
    context.kind ===
    'space'

  const isEncounter =
    context.kind ===
    'encounter'

  const canPublish =
    (
      isSpace ||
      isEncounter
    ) &&
    Boolean(
      sellerId
    )

  const spaceId =
    isSpace ||
    isEncounter
      ? Number(
          context.spaceId
        )
      : null

  const encounterId =
    isEncounter
      ? Number(
          context.encounterId
        )
      : null

  const effectiveEncounterId =
    encounterId ||
    suggestedEncounterId ||
    null

  const effectiveContributionType =
    effectiveEncounterId
      ? (
          contributionType ||
          suggestedContributionType ||
          null
        )
      : null

  /*
   * Encounter exige actualmente
   * contribution type.
   *
   * Space no.
   */
  const needsContributionType =
    isEncounter ||
    Boolean(
      suggestedEncounterId
    )

  /* ---------------------------------------------------------------------- */
  /*                             PRODUCT                                    */
  /* ---------------------------------------------------------------------- */

  const selectedProduct =
    useMemo(
      () =>
        selectedProductId
          ? products.find(
              product =>
                product.id ===
                selectedProductId
            ) || null
          : null,
      [
        products,
        selectedProductId,
      ]
    )

  /*
   * No cargamos productos hasta que
   * realmente pueda utilizarse premium.
   *
   * Así no metemos un request global
   * innecesario en AIMasterWidget.
   */
  useEffect(() => {
    if (
      !open ||
      !canUsePremiumContent ||
      !sellerId
    ) {
      return
    }

    let cancelled =
      false

    async function loadProducts() {
      try {
        setProductsLoading(
          true
        )

        const response =
          await fetch(
            `/api/community/feed/${sellerId}/products`,
            {
              cache:
                'no-store',

              credentials:
                'include',
            }
          )

        const json =
          await response
            .json()
            .catch(
              () =>
                null
            )

        if (
          !response.ok
        ) {
          throw new Error(
            json?.error ||
              'No se pudieron cargar los productos.'
          )
        }

        if (cancelled) {
          return
        }

        setProducts(
          Array.isArray(
            json?.products
          )
            ? json.products
            : []
        )
      } catch (
        loadError
      ) {
        console.error(
          '[ContextPublishComposer] product load error',
          loadError
        )

        if (
          !cancelled
        ) {
          setProducts([])
        }
      } finally {
        if (
          !cancelled
        ) {
          setProductsLoading(
            false
          )
        }
      }
    }

    void loadProducts()

    return () => {
      cancelled =
        true
    }
  }, [
    open,
    canUsePremiumContent,
    sellerId,
  ])

  /* ---------------------------------------------------------------------- */
  /*                              FILES                                     */
  /* ---------------------------------------------------------------------- */

  const addFiles =
    useCallback(
      (
        incoming:
          File[] |
          FileList
      ) => {
        const incomingFiles =
          Array.from(
            incoming || []
          )

        if (
          !incomingFiles.length
        ) {
          return
        }

        setFiles(
          previous => {
            const existingKeys =
              new Set(
                previous.map(
                  item =>
                    fileIdentity(
                      item.file
                    )
                )
              )

            const additions:
              ContextPublishFile[] =
              []

            for (
              const file
              of incomingFiles
            ) {
              if (
                previous.length +
                  additions.length >=
                MAX_FILES
              ) {
                break
              }

              if (
                !isAcceptedFile(
                  file
                )
              ) {
                continue
              }

              const key =
                fileIdentity(
                  file
                )

              if (
                existingKeys.has(
                  key
                )
              ) {
                continue
              }

              existingKeys.add(
                key
              )

              additions.push(
                makeComposerFile(
                  file
                )
              )
            }

            return [
              ...previous,
              ...additions,
            ]
          }
        )
      },
      []
    )

  const removeFile =
    useCallback(
      (
        fileId:
          string
      ) => {
        setFiles(
          previous =>
            previous.filter(
              item => {
                if (
                  item.id !==
                  fileId
                ) {
                  return true
                }

                if (
                  item.previewUrl
                ) {
                  URL.revokeObjectURL(
                    item.previewUrl
                  )
                }

                return false
              }
            )
        )
      },
      []
    )

  const clearFiles =
    useCallback(
      () => {
        setFiles(
          previous => {
            for (
              const item
              of previous
            ) {
              if (
                item.previewUrl
              ) {
                URL.revokeObjectURL(
                  item.previewUrl
                )
              }
            }

            return []
          }
        )
      },
      []
    )

  useEffect(() => {
    return () => {
      for (
        const item
        of filesRef.current
      ) {
        if (
          item.previewUrl
        ) {
          URL.revokeObjectURL(
            item.previewUrl
          )
        }
      }
    }
  }, [])

  /* ---------------------------------------------------------------------- */
  /*                             VALIDATION                                 */
  /* ---------------------------------------------------------------------- */

  const validation =
    useMemo(() => {
      if (!canPublish) {
        return {
          valid:
            false,

          reason:
            'Este contexto no permite publicar.',
        }
      }

      if (
        !content.trim()
      ) {
        return {
          valid:
            false,

          reason:
            'Escribe algo para publicar.',
        }
      }

      if (
        needsContributionType &&
        !effectiveContributionType
      ) {
        return {
          valid: false,
          reason:
            'Selecciona el tipo de aporte.',
        }
      }

      return {
        valid:
          true,

        reason:
          null,
      }
    }, [
      canPublish,
      content,
      needsContributionType,
      effectiveContributionType,
      contributionType,
    ])

  const canSubmit =
    validation.valid &&
    status !==
      'publishing'

  /* ---------------------------------------------------------------------- */
  /*                              RESET                                     */
  /* ---------------------------------------------------------------------- */

  const reset =
    useCallback(
      () => {
        clearFiles()

        setTitle('')
        setContent('')

        setContributionType(
          null
        )

        setSuggestedEncounterId(
          null
        )

        setSuggestedContributionType(
          null
        )

        setSelectedProductId(
          null
        )

        setStatus(
          'idle'
        )

        setError(
          null
        )
      },
      [
        clearFiles,
      ]
    )

  const closeComposer =
    useCallback(
      (
        options?: {
          reset?:
            boolean
        }
      ) => {
        setOpen(
          false
        )

        setError(
          null
        )

        if (
          options?.reset
        ) {
          reset()
        }
      },
      [
        reset,
      ]
    )

  const openComposer =
    useCallback(
      (
        args?:
          OpenContextPublishComposerArgs
      ) => {
        if (
          !canPublish
        ) {
          return false
        }

        setError(
          null
        )

        setStatus(
          'idle'
        )

        if (
          args?.initialContent !==
          undefined
        ) {
          setContent(
            args.initialContent
          )
        }

        if (
          args?.contributionType !==
          undefined
        ) {
          setContributionType(
            args.contributionType
          )
        }

        setOpen(
          true
        )

        return true
      },
      [
        canPublish,
      ]
    )

  /* ---------------------------------------------------------------------- */
  /*                               EMOJI                                    */
  /* ---------------------------------------------------------------------- */

  const appendEmoji =
    useCallback(
      (
        emoji:
          string
      ) => {
        if (!emoji) {
          return
        }

        setContent(
          previous =>
            `${previous}${emoji}`
        )
      },
      []
    )

  /* ---------------------------------------------------------------------- */
  /*                              PUBLISH                                   */
  /* ---------------------------------------------------------------------- */

  const publish =
    useCallback(
      async () => {
        if (
          !sellerId ||
          !spaceId
        ) {
          const message =
            'No se encontró el Space actual.'

          setError(
            message
          )

          return null
        }

        const cleanContent =
          content.trim()

        if (
          !cleanContent
        ) {
          const message =
            'Escribe algo para publicar.'

          setError(
            message
          )

          return null
        }

        if (
          effectiveEncounterId &&
          !effectiveContributionType
        ) {
          const message =
            'Selecciona el tipo de aporte.'

          setError(
            message
          )

          return null
        }

        if (
          status ===
          'publishing'
        ) {
          return null
        }

        setStatus(
          'publishing'
        )

        setError(
          null
        )

        try {
          /*
           * Mismo flujo de upload que
           * Community.
           */
          const uploadedAttachments =
            await Promise.all(
              files.map(
                item =>
                  uploadCommunityFile(
                    item.file,
                    Number(
                      sellerId
                    )
                  )
              )
            )

          /*
           * IMPORTANTE:
           *
           * El Space actual YA es el
           * destino. No hay channel
           * selector.
           */
          const payload = {
            title:
              title.trim(),

            comment:
              cleanContent,

            channelId:
              spaceId,

            category_id:
              spaceId,

            /*
             * El backend actual de
             * Community acepta channel,
             * pero aquí no necesitamos
             * conocer el nombre.
             */
            channel:
              '',

            product_id:
              selectedProductId,

            attachments:
              uploadedAttachments.map(
                attachment => ({
                  fileattach:
                    attachment.fileattach,

                  fileattach_orig:
                    attachment.fileattach_orig,
                })
              ),

            /*
             * Contexto Encounter.
             *
             * En Space ambos son null.
             */
            encounterId:
              effectiveEncounterId,

            contributionType:
              effectiveContributionType,
          }

          const response =
            await fetch(
              `/api/community/feed/${sellerId}`,
              {
                method:
                  'POST',

                credentials:
                  'include',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body:
                  JSON.stringify(
                    payload
                  ),
              }
            )

          const json =
            await response
              .json()
              .catch(
                () =>
                  null
              )

          if (
            !response.ok ||
            !json?.post
          ) {
            throw new Error(
              json?.error ||
                'No se pudo publicar.'
            )
          }

          setStatus(
            'success'
          )

          const result:
            ContextPublishResult =
            {
              post:
                json.post,

              spaceId:
                Number(
                  spaceId
                ),

              encounterId:
                effectiveEncounterId,

              contributionType:
                effectiveContributionType,
            }

          /*
           * Surface-agnostic.
           *
           * Space/Encounter pueden
           * escuchar esto y revalidar
           * su feed.
           */
          if (
            typeof window !==
            'undefined'
          ) {
            window.dispatchEvent(
              new CustomEvent(
                'community:post-created',
                {
                  detail: {
                    post:
                      json.post,

                    sellerId:
                      Number(
                        sellerId
                      ),

                    spaceId,

                    encounterId:
                      effectiveEncounterId,

                    contributionType:
                      effectiveContributionType,
                  },
                }
              )
            )
          }

          onPublished?.(result)

          reset()

          return result
        } catch (
          publishError:
            any
        ) {
          console.error(
            '[ContextPublishComposer] publish error',
            publishError
          )

          const message =
            String(
              publishError?.message ||
                'No se pudo publicar.'
            )

          setStatus(
            'error'
          )

          setError(
            message
          )

          return null
        }
      },
      [
        sellerId,
        spaceId,
        encounterId,
        effectiveEncounterId,
        effectiveContributionType,
        title,
        content,
        contributionType,
        selectedProductId,
        files,
        status,
        onPublished,
      ]
    )

  /* ---------------------------------------------------------------------- */
  /*                              ROUTE CHANGE                              */
  /* ---------------------------------------------------------------------- */

  /*
   * Si el usuario navega a otro
   * Space/Encounter mientras está
   * abierto, NO queremos publicar
   * accidentalmente en el destino
   * nuevo con un draft viejo.
   */
  const contextKey =
    `${context.kind}:${
      spaceId || 0
    }:${
      encounterId || 0
    }`

  const previousContextKeyRef =
    useRef(
      contextKey
    )

  useEffect(() => {
    if (
      previousContextKeyRef
        .current ===
      contextKey
    ) {
      return
    }

    previousContextKeyRef.current =
      contextKey

    setOpen(
      false
    )

    reset()
  }, [
    contextKey,
    reset,
  ])

  /* ---------------------------------------------------------------------- */
  /*                                RETURN                                  */
  /* ---------------------------------------------------------------------- */

  return {
    /* Context */
    canPublish,
    isSpace,
    isEncounter,

    spaceId,
    encounterId,

    needsContributionType,

    /* Surface */
    open,
    openComposer,
    closeComposer,

    /* Fields */
    title,
    setTitle,

    content,
    setContent,

    contributionType,
    setContributionType,

    suggestedEncounterId,
    setSuggestedEncounterId,

    suggestedContributionType,
    setSuggestedContributionType,

    /* Files */
    files,
    addFiles,
    removeFile,
    clearFiles,

    maxFiles:
      MAX_FILES,

    /* Premium */
    products,
    productsLoading,

    selectedProduct,
    selectedProductId,
    setSelectedProductId,

    canUsePremiumContent,

    /* Helpers */
    appendEmoji,

    /* Validation */
    validation,
    canSubmit,

    /* Request */
    status,
    error,

    publishing:
      status ===
      'publishing',

    publish,

    /* Lifecycle */
    reset,
  }
}